import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { trackEvent } from "@/hooks/useAnalytics";
import { Flame, Lock, ArrowRight, Clock, BookOpen, Award, CheckCircle2, Sparkles } from "lucide-react";
import { RiserDiagram } from "@/components/RiserDiagram";
import { RISER_DIAGRAMS } from "@/data/riserDiagrams";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SampleLesson() {
  useDocumentMeta({
    title: "Free sample lesson — Fire Detection Basics",
    description: "Try the first 4 sections of M1-L1 from APM Fire Alarm Exam Prep free. No sign-up required. 140 hours total in the full NICET Level 1 · 2 · 3 + NYC S-98 curriculum.",
    path: "/sample",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/sample-lesson`);
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-6 py-20 text-zinc-500">Loading sample…</div>
      </div>
    );
  }
  if (!data) return null;

  const { module: mod, lesson, teaser } = data;

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal public header */}
      <header className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-red-600 grid place-items-center">
              <Flame className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[15px] font-bold tracking-tight">APM Fire Alarm Exam Prep</div>
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">Free sample lesson</div>
            </div>
          </Link>
          <Link to="/register" data-testid="sample-enroll-nav" className="text-sm font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1" onClick={() => trackEvent("enroll_click", { location: "sample_header" })}>
            Enroll now <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="sample-lesson-page">
        {/* Breadcrumb */}
        <div className="overline mb-2"><span className="red-bar" />Free preview · {mod.title}</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-4" data-testid="sample-lesson-title">
          {lesson.title}
        </h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-zinc-600 font-mono mb-10">
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {lesson.hours}h lesson</span>
          <span className="inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {lesson.sections_total} sections · {lesson.sections_shown} free</span>
          <span className="inline-flex items-center gap-1.5 text-red-600"><Sparkles className="w-3.5 h-3.5" /> No sign-up required</span>
        </div>

        {/* Outcomes */}
        {lesson.outcomes?.length > 0 && (
          <div className="card-tech p-6 mb-10" data-testid="sample-lesson-outcomes">
            <div className="overline mb-3"><span className="red-bar" />What you'll learn in this lesson</div>
            <ul className="space-y-2">
              {lesson.outcomes.map((o, i) => (
                <li key={`outcome-${i}-${o.slice(0, 30)}`} className="flex items-start gap-2 text-sm text-zinc-700">
                  <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  {o}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Free sections */}
        <div className="space-y-10 mb-16" data-testid="sample-lesson-sections">
          {lesson.sections.map((s, i) => (
            <section key={`sec-${i}-${s.heading?.slice(0, 30) || ''}`} className="border-l-4 border-red-600 pl-5">
              <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">{s.heading}</h2>
              <p className="text-zinc-700 leading-relaxed whitespace-pre-line">{s.body}</p>
            </section>
          ))}
        </div>

        {/* Locked CTA */}
        <div className="relative border-4 border-red-600 bg-red-50/40 p-8 sm:p-10" data-testid="sample-lesson-paywall">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-600 text-white grid place-items-center">
              <Lock className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div>
              <div className="overline"><span className="red-bar" />Preview ends here</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                {lesson.sections_total - lesson.sections_shown} more sections + quiz in this lesson alone.
              </h2>
            </div>
          </div>

          <p className="text-zinc-700 mb-5 leading-relaxed">
            The full NICET Foundation curriculum continues with {teaser.total_hours} hours across {teaser.module_count} modules.
            Still ahead in this lesson:
          </p>
          {lesson.locked_sections?.length > 0 && (
            <ul className="space-y-1.5 mb-6 font-mono text-sm text-zinc-600" data-testid="sample-lesson-locked-list">
              {lesson.locked_sections.slice(0, 8).map((h, i) => (
                <li key={`locked-${i}-${h}`} className="flex items-center gap-2">
                  <Lock className="w-3 h-3 text-zinc-400" /> {h}
                </li>
              ))}
              {lesson.locked_sections.length > 8 && (
                <li className="text-zinc-500">+ {lesson.locked_sections.length - 8} more…</li>
              )}
            </ul>
          )}

          <div className="grid sm:grid-cols-3 gap-4 mb-6 pt-4 border-t-2 border-red-200">
            <div>
              <div className="overline">Total</div>
              <div className="font-heading text-2xl font-bold">{teaser.total_hours} hrs</div>
              <div className="text-xs text-zinc-500 font-mono">NICET L1 · L2 · L3 · S-98</div>
            </div>
            <div>
              <div className="overline">Certificate</div>
              <div className="font-heading text-2xl font-bold">QR-verified</div>
              <div className="text-xs text-zinc-500 font-mono">On 70%+ final pass</div>
            </div>
            <div>
              <div className="overline">One-time price</div>
              <div className="font-heading text-2xl font-bold">${teaser.price_usd}</div>
              <div className="text-xs text-zinc-500 font-mono">Lifetime access</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="flex-1" data-testid="sample-enroll-btn" onClick={() => trackEvent("enroll_click", { location: "sample_paywall" })}>
              <button className="btn-primary w-full">
                <Award className="w-4 h-4" /> Enroll now <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/" className="flex-1">
              <button className="btn-outline w-full">Back to homepage</button>
            </Link>
          </div>
        </div>
      </main>

      {/* Free interactive teaser — small commercial riser */}
      <section className="border-t-2 border-zinc-100 bg-zinc-50/50">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-14" data-testid="sample-riser-teaser">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-red-600" />
            <div className="overline"><span className="red-bar" />Free interactive demo</div>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mb-3">
            Click any device. See the citation.
          </h2>
          <p className="text-zinc-700 max-w-2xl leading-relaxed mb-6">
            This is the same interactive riser tool inside the paid course — try it now. Click a smoke
            detector, pull station, or the FACU. Each device tells you what it is, the NFPA 72 reference,
            and which lesson it's covered in. The full course has 4 buildings (commercial, high-rise,
            healthcare, mixed-use) plus a working FACU panel simulator.
          </p>
          <RiserDiagram diagram={RISER_DIAGRAMS[0]} freeMode />
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="flex-1" onClick={() => trackEvent("enroll_click", { location: "sample_riser_teaser" })}>
              <button className="btn-primary w-full">
                Unlock all 4 risers + panel simulator <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-zinc-500 font-mono flex justify-between">
          <span>© APM Fire Alarm Exam Prep</span>
          <Link to="/login" className="hover:text-red-600">Already enrolled? Log in →</Link>
        </div>
      </footer>
    </div>
  );
}
